import React, { useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ResolveImage from 'components/ResolveImage';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { PROFILE_TABLE_LABELS, TABLE_KEY_VALUES } from './utils';
import { getHrefFromUrl, isUrl } from 'utils';
import { User } from 'models/User';
import CheckcircleIcon from 'icons/Checkcircle.icon';
import XcircleIcon from 'icons/Xcircle.icon';
import get from 'lodash.get';
import { getImgUrl } from 'components/ResolveImage/utils';
import HelmetWrapper from 'components/HelmetWrapper';
import { ListingLayout } from '../ListingLayout';
import LeadContext from '../../providers/Lead/Lead.context';
import { Button } from 'ncoded-component-library';
import PencilIcon from 'icons/Pencil.icon';
import { useNavigate } from 'react-router-dom';
import { PROFILE_PERSONAL_DETAILS_ROUTE } from 'data/global';

import './BuyerProfileCard.styles.scss';
import './BuyerProfileCard.styles.responsive.scss';

type BuyerProfileCardProps = {
  className?: string;
};

const BuyerProfileCard: React.FC<BuyerProfileCardProps> = (props) => {
  const { className } = props;

  const [hasMissingKeyValues, setHasMissingKeyValues] = useState(false);

  const { currentUser } = useContext(CurrentUserContext);
  const { dealRoom } = useContext(LeadContext);

  const classes = classNames('buyer-profile-card', className);

  const { t } = useTranslation();

  const navigate = useNavigate();

  const tableContent = useMemo(
    () =>
      Object.entries(PROFILE_TABLE_LABELS).map(([key, value]) => {
        const sellerValue =
          key === 'company'
            ? get(currentUser, [key, 'name'])
            : Array.isArray(currentUser[key as keyof User])
            ? (currentUser[key as keyof User] as Array<string>).reduce(
                (acc, val, index) =>
                  (acc += `${val}${
                    index <
                    (currentUser[key as keyof User] as string).length - 1
                      ? ', '
                      : ''
                  }`),
                '',
              )
            : (currentUser[key as keyof User] as string);

        if (!sellerValue) setHasMissingKeyValues(true);

        return (
          <tr key={key}>
            <td className={classNames({ invalid: !sellerValue })}>
              {sellerValue ? (
                <CheckcircleIcon />
              ) : (
                <XcircleIcon
                  className={classNames({
                    'key-value': TABLE_KEY_VALUES.includes(value),
                  })}
                />
              )}
              {t(value)}
            </td>
            <td>
              {sellerValue ? (
                isUrl(sellerValue) ? (
                  <a href={getHrefFromUrl(sellerValue)}>{t('viewLink')}</a>
                ) : (
                  sellerValue
                )
              ) : (
                '-'
              )}
            </td>
          </tr>
        );
      }),
    [currentUser, t],
  );

  return (
    <ListingLayout isBuying>
      <div className={classes}>
        <HelmetWrapper title={`Profile - ${dealRoom?.portfolioName}`} />

        <div className="buyer-profile-card__heading">
          <p>{t('howDoesSellerSeeYou')}</p>

          {hasMissingKeyValues && (
            <Button
              aria-label="Edit"
              variant="icon"
              icon={PencilIcon}
              onClick={() => navigate(PROFILE_PERSONAL_DETAILS_ROUTE)}
            />
          )}
        </div>

        <div className="buyer-profile-card__content-container">
          <ResolveImage
            asAvatar
            alt="Buyer"
            src={getImgUrl(currentUser?.profileImage?.url, '100')}
          />

          <table>
            <tbody>{tableContent}</tbody>
          </table>
        </div>
      </div>
    </ListingLayout>
  );
};

export default BuyerProfileCard;
